import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Select, 
  Typography,
  Row,
  Col,
  message,
  InputNumber,
  Upload,
  Card,
  Tag,
  Space,
  Divider
} from 'antd';
import { 
  UploadOutlined, 
  DeleteOutlined,
  TrophyOutlined,
  StarOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminTrophyBadgeApi, fileApi } from '@/services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AdminTrophyBadgeMasterDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { trophyBadgeId } = useParams<{ trophyBadgeId: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [iconUrl, setIconUrl] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('trophy');
  const [selectedSubtype, setSelectedSubtype] = useState<string | undefined>();
  const [previewName, setPreviewName] = useState<string>('');
  const [previewDescription, setPreviewDescription] = useState<string>('');
  const fromPath = (location.state as { from?: string } | null)?.from;

  const isEditMode = trophyBadgeId && trophyBadgeId !== 'add';


  useEffect(() => {
    if (isEditMode) {
      fetchDetail();
    }
  }, [trophyBadgeId]);

  const fetchDetail = async () => {
    if (!trophyBadgeId || trophyBadgeId === 'add') return;

    const numericId = Number(trophyBadgeId);
    if (isNaN(numericId)) {
      message.error('잘못된 ID입니다.');
      return;
    }

    setLoading(true);
    try {
      const response = await adminTrophyBadgeApi.getMasterDetail(numericId);
      const data = response?.data || response;
      
      form.setFieldsValue({
        name: data.name,
        description: data.description,
        iconUrl: data.iconUrl,
        type: data.type,
        badgeSubtype: data.badgeSubtype,
        unitStreakCount: data.unitStreakCount,
      });

      setIconUrl(data.iconUrl || '');
      setSelectedType(data.type || 'trophy');
      setSelectedSubtype(data.badgeSubtype || undefined);
      setPreviewName(data.name || '');
      setPreviewDescription(data.description || '');
    } catch (error) {
      message.error('데이터를 불러오는데 실패했습니다.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    setUploading(true);
    try {
      const response = await fileApi.upload(file, 'trophy-badge');
      const url = response?.data?.url || response?.url || '';
      setIconUrl(url);
      form.setFieldsValue({ iconUrl: url });
      message.success('이미지가 업로드되었습니다.');
      return false; // prevent default upload behavior
    } catch (error) {
      message.error('이미지 업로드에 실패했습니다.');
      console.error(error);
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setIconUrl('');
    form.setFieldsValue({ iconUrl: '' });
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      if (isEditMode) {
        await adminTrophyBadgeApi.updateMaster(Number(trophyBadgeId), values);
        message.success('수정되었습니다.');
      } else {
        await adminTrophyBadgeApi.createMaster(values);
        message.success('등록되었습니다.');
      }
      navigate(fromPath || '/admin/trophy-badge-master');
    } catch (error) {
      message.error(isEditMode ? '수정에 실패했습니다.' : '등록에 실패했습니다.');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleBackToList = () => {
    navigate(fromPath || '/admin/trophy-badge-master'); // 이전 페이지로 돌아가기 (필터 유지)
  };

  const handleCancel = () => {
    navigate(fromPath || '/admin/trophy-badge-master'); // 이전 페이지로 돌아가기 (필터 유지)
  };

  return (
    <AdminLayout>
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        <Space style={{ marginBottom: 16 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBackToList}
            size="large"
          >
            목록으로
          </Button>
        </Space>

        <div style={{ marginBottom: 24 }}>
          <Title level={2}>
            {isEditMode ? '트로피/뱃지 수정' : '트로피/뱃지 등록'}
          </Title>
        </div>

        <Row gutter={24}>
          {/* 왼쪽: 입력 폼 */}
          <Col span={16}>
            <Card>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                onValuesChange={(changedValues, allValues) => {
                  if (changedValues.type !== undefined) {
                    setSelectedType(changedValues.type);
                  }
                  if (changedValues.badgeSubtype !== undefined) {
                    setSelectedSubtype(changedValues.badgeSubtype);
                    if (changedValues.badgeSubtype !== 'unit_streak') {
                      form.setFieldsValue({ unitStreakCount: null });
                    }
                  }
                  if (changedValues.iconUrl !== undefined) {
                    setIconUrl(changedValues.iconUrl);
                  }
                  if (changedValues.name !== undefined) {
                    setPreviewName(changedValues.name);
                  }
                  if (changedValues.description !== undefined) {
                    setPreviewDescription(changedValues.description);
                  }
                }}
                initialValues={{
                  type: 'trophy',
                }}
              >
                    <Form.Item
                      label="유형"
                      name="type"
                      rules={[{ required: true, message: '유형을 선택해주세요' }]}
                    >
                      <Select 
                        size="large" 
                        placeholder="유형을 선택하세요"
                        onChange={(value) => setSelectedType(value)}
                      >
                        <Option value="trophy">
                          <Space>
                            <TrophyOutlined style={{ color: '#faad14' }} />
                            트로피
                          </Space>
                        </Option>
                        <Option value="badge">
                          <Space>
                            <StarOutlined style={{ color: '#1890ff' }} />
                            뱃지
                          </Space>
                        </Option>
                      </Select>
                    </Form.Item>

                <Form.Item
                  label="이름"
                  name="name"
                  rules={[{ required: true, message: '이름을 입력해주세요' }]}
                >
                  <Input size="large" placeholder="예: 금상, 은상, 완벽한 학습자" />
                </Form.Item>

                <Form.Item
                  label="설명"
                  name="description"
                  rules={[{ required: true, message: '설명을 입력해주세요' }]}
                >
                  <TextArea
                    rows={4}
                    placeholder="트로피/뱃지에 대한 설명을 입력하세요"
                    size="large"
                  />
                </Form.Item>

                <Divider orientation="left">아이콘 이미지</Divider>

                <Form.Item
                  label="아이콘 업로드"
                  extra={<Text type="secondary">PNG, JPG 파일 (권장 크기: 256x256)</Text>}
                >
                  <Upload
                    accept="image/*"
                    maxCount={1}
                    showUploadList={false}
                    beforeUpload={handleImageUpload}
                    disabled={uploading}
                  >
                    <Button icon={<UploadOutlined />} loading={uploading} size="large">
                      이미지 선택
                    </Button>
                  </Upload>
                </Form.Item>

                <Form.Item
                  label="아이콘 URL (직접 입력)"
                  name="iconUrl"
                  rules={[{ required: true, message: '아이콘을 업로드하거나 URL을 입력해주세요' }]}
                >
                  <Input 
                    size="large" 
                    placeholder="https://example.com/icon.png"
                    onChange={(e) => setIconUrl(e.target.value)}
                  />
                </Form.Item>

                {/* 뱃지 세부 설정 (뱃지일 때만 표시) */}
                {selectedType === 'badge' && (
                  <>
                    <Divider orientation="left">뱃지 세부 설정</Divider>
                    
                    <Form.Item
                      label="뱃지 세부 유형"
                      name="badgeSubtype"
                      extra={<Text type="secondary">자동 부여 뱃지인 경우 해당 유형을 선택하세요</Text>}
                    >
                      <Select 
                        size="large" 
                        placeholder="세부 유형 선택"
                        allowClear
                      >
                        <Option value="textbook_complete">교재 완료</Option>
                        <Option value="recording_score">녹음 채점 만점</Option>
                        <Option value="unit_streak">유닛 연속 완료 (요정/마법사/신)</Option>
                        <Option value="word_streak">단어 연속 완료 (로그아웃 없이)</Option>
                        <Option value="perfect_clear">숙제 Perfect Clear</Option>
                        <Option value="video_quiz">동영상 퀴즈 오답없이 통과</Option>
                        <Option value="wordbook">단어장 완료</Option>
                        <Option value="wordbook_perfect">단어장 퍼펙트</Option>
                        <Option value="manual">수동 부여 전용</Option>
                      </Select>
                </Form.Item>

                {selectedSubtype === 'unit_streak' && (
                  <Form.Item
                        label="연속 완료 기준 개수"
                        name="unitStreakCount"
                        rules={[{ required: true, message: '연속 완료 기준을 입력해주세요' }]}
                        extra={<Text type="secondary">
                          유닛 연속 완료 기준 (예: 3, 5, 10). 해당 값마다 누적 뱃지가 부여됩니다.
                        </Text>}
                  >
                        <InputNumber
                      size="large"
                          style={{ width: '100%' }}
                          min={1}
                          max={1000}
                          placeholder="예: 3 또는 5 또는 10"
                    />
                  </Form.Item>
                )}
                  </>
                )}

                <Form.Item>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <Button size="large" onClick={handleCancel}>
                      취소
                    </Button>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      size="large"
                      loading={saving}
                    >
                      {isEditMode ? '수정' : '등록'}
                    </Button>
                  </div>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          {/* 오른쪽: 미리보기 */}
          <Col span={8}>
            <Card 
              title="미리보기" 
              style={{ position: 'sticky', top: 24 }}
              extra={
                <Tag color={selectedType === 'trophy' ? 'gold' : 'blue'}>
                  {selectedType === 'trophy' ? '트로피' : '뱃지'}
                </Tag>
              }
            >
              <div style={{ textAlign: 'center' }}>
                {/* 아이콘 이미지 미리보기 */}
                <div 
                  style={{ 
                    width: '200px', 
                    height: '200px',
                    margin: '0 auto 20px',
                    border: '3px solid #d9d9d9',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fafafa',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {iconUrl ? (
                    <>
                      <img 
                        src={iconUrl} 
                        alt="아이콘 미리보기" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleRemoveImage}
                        style={{ 
                          position: 'absolute',
                          top: 8,
                          right: 8
                        }}
                        size="small"
                      >
                        삭제
                      </Button>
                    </>
                  ) : (
                    <div style={{ color: '#bfbfbf', fontSize: '14px' }}>
                      {selectedType === 'trophy' ? <TrophyOutlined style={{ fontSize: '48px', marginBottom: '8px', display: 'block' }} /> : <StarOutlined style={{ fontSize: '48px', marginBottom: '8px', display: 'block' }} />}
                      이미지를 업로드하세요
                    </div>
                  )}
                </div>

                {/* 이름 표시 */}
                <Title level={4} style={{ marginBottom: '8px' }}>
                  {previewName || '트로피/뱃지 이름'}
                </Title>

                {/* 설명 표시 */}
                <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                  {previewDescription || '설명을 입력하세요'}
                </Text>

                <Divider />

                {/* 안내 문구 */}
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    💡 이미지는 투명 배경 PNG를 권장합니다
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    🎨 등급별로 테두리 색상이 자동 적용됩니다
                  </Text>
                </Space>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </AdminLayout>
  );
};

export default AdminTrophyBadgeMasterDetail;

